// --- Imports ---
import { Box, useMediaQuery, useTheme } from "@mui/material";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import React from "react";

// --- Hero Picture Component ---
export default function HeroPicture() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme?.breakpoints?.down("md"));

  // --- Core Framer Motion Values ---
  const x = useMotionValue(0);
  const y = useMotionValue(0);

  // --- Smooth Physics ---
  const springConfig = { stiffness: 100, damping: 20 };
  const mouseXSpring = useSpring(x, springConfig);
  const mouseYSpring = useSpring(y, springConfig);

  // --- 3D Transformations ---
  const rotateX = useTransform(mouseYSpring, [-0.5, 0.5], ["18deg", "-18deg"]);
  const rotateY = useTransform(mouseXSpring, [-0.5, 0.5], ["-18deg", "18deg"]);

  // --- Inner Image Parallax (Moves opposite to frame) ---
  const imageX = useTransform(mouseXSpring, [-0.5, 0.5], ["-10%", "10%"]);
  const imageY = useTransform(mouseYSpring, [-0.5, 0.5], ["-10%", "10%"]);

  // --- Holographic Glare Position ---
  const glareX = useTransform(mouseXSpring, [-0.5, 0.5], ["150%", "-50%"]);
  const glareY = useTransform(mouseYSpring, [-0.5, 0.5], ["150%", "-50%"]);

  // --- Handlers ---
  const handleMouseMove = (e) => {
    const rect = e?.currentTarget?.getBoundingClientRect();
    if (!rect) return;
    x.set((e?.clientX - rect?.left) / rect?.width - 0.5);
    y.set((e?.clientY - rect?.top) / rect?.height - 0.5);
  };

  const handleMouseLeave = () => {
    x.set(0);
    y.set(0);
  };

  return (
    <Box
      sx={{
        position: "relative",
        zIndex: 2,
        perspective: "2000px",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        flex: 1,
        width: "100%",
        height: { xs: "500px", md: "600px", lg: "700px" },
      }}
    >
      {/* --- Orbiting Rings --- */}
      {[1, 2, 3]?.map((ring, index) => (
        <Box
          key={ring}
          component={motion.div}
          animate={{ rotate: 360, scale: [1, 1.05, 1] }}
          transition={{
            rotate: {
              duration: 20 + index * 10,
              repeat: Infinity,
              ease: "linear",
              direction: index % 2 === 0 ? "normal" : "reverse",
            },
            scale: {
              duration: 4,
              repeat: Infinity,
              ease: "easeInOut",
              delay: index,
            },
          }}
          sx={{
            position: "absolute",
            width: {
              xs: 300 + index * 60,
              md: 400 + index * 80,
              lg: 500 + index * 100,
            },
            height: {
              xs: 300 + index * 60,
              md: 400 + index * 80,
              lg: 500 + index * 100,
            },
            borderRadius: "50%",
            border: `${1 + index}px solid ${theme?.palette?.primary?.main}${30 - index * 10}`,
            borderTopColor: "transparent",
            borderBottomColor:
              index === 1 ? theme?.palette?.secondary?.main : "transparent",
            zIndex: 0,
          }}
        />
      ))}

      {/* --- Main 3D Card --- */}
      <motion.div
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        style={{
          rotateX,
          rotateY,
          transformStyle: "preserve-3d",
          zIndex: 10,
          position: "relative",
        }}
      >
        <Box
          sx={{
            width: { xs: "280px", md: "340px", lg: "440px" },
            height: { xs: "380px", md: "460px", lg: "600px" },
            borderRadius: "30px",
            position: "relative",
            overflow: "hidden",
            boxShadow: `
              0 30px 60px -15px rgba(0,0,0,0.8),
              0 0 40px -10px ${theme?.palette?.primary?.main}50,
              inset 0 0 0 1px rgba(255,255,255,0.2)
            `,
            backgroundColor: "#111",
            transform: "translateZ(50px)",
          }}
        >
          {/* --- Parallax Inner Image --- */}
          <motion.img
            src="/media/pouriya-sabouri.webp"
            alt="پوریا صبوری"
            style={{
              width: "120%",
              height: "120%",
              objectFit: "cover",
              position: "absolute",
              top: "-10%",
              left: "-10%",
              x: imageX,
              y: imageY,
              filter: "contrast(1.1) brightness(0.9)",
            }}
          />

          {/* --- Interactive Holographic Glare --- */}
          <motion.div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "200%",
              height: "200%",
              background: `linear-gradient(135deg, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0) 40%, rgba(255,255,255,0) 100%)`,
              x: glareX,
              y: glareY,
              pointerEvents: "none",
              mixBlendMode: "overlay",
            }}
          />

          {/* --- Edge Glow --- */}
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              borderRadius: "30px",
              boxShadow: `inset 0 0 30px rgba(0,0,0,0.8)`,
              pointerEvents: "none",
            }}
          />
        </Box>

        {/* --- Floating 3D Elements around the card --- */}
        <Box
          component={motion.div}
          animate={{ y: [-20, 20, -20], rotateZ: [0, 90, 0] }}
          transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
          sx={{
            position: "absolute",
            top: "-10%",
            right: "-15%",
            width: "80px",
            height: "80px",
            borderRadius: "20px",
            background: `linear-gradient(135deg, ${theme?.palette?.primary?.main}, ${theme?.palette?.secondary?.main})`,
            boxShadow: `0 15px 35px ${theme?.palette?.primary?.main}60`,
            transform: "translateZ(100px)",
            display: { xs: "none", md: "block" },
          }}
        />

        <Box
          component={motion.div}
          animate={{ y: [20, -20, 20], rotateZ: [0, -90, 0] }}
          transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
          sx={{
            position: "absolute",
            bottom: "-5%",
            left: "-10%",
            width: "60px",
            height: "60px",
            borderRadius: "50%",
            backdropFilter: "blur(10px)",
            border: `2px solid ${theme?.palette?.secondary?.main}`,
            background: `${theme?.palette?.secondary?.main}40`,
            boxShadow: `0 15px 35px ${theme?.palette?.secondary?.main}60`,
            transform: "translateZ(120px)",
            display: { xs: "none", md: "block" },
          }}
        />
      </motion.div>
    </Box>
  );
}
